import React, { useMemo, useState } from 'react';
import { EditIcon } from '@chakra-ui/icons';
import { Button, Flex, Icon, useDisclosure } from '@chakra-ui/react';

import { ErrorMessages, FullPageLoader, Modal, PageHeading } from '../../../../../common/components';
import { useTranslations } from '../../../../../contexts/LocalizationContext';
import { useRestaurantDepositSettings } from '../../../../../services/deposit';
import { convertResponseToFormValues } from '../../utils/deposit.helpers';
import { DepositSettingsHistory } from '../DepositSettingsHistory/DepositSettingsHistory';
import { UpdateDepositSettings } from '../UpdateDepositSettings/UpdateDepositSettings';

type DepositSettingsProps = {
  restaurantId: string;
};

export const DepositSettings = ({ restaurantId }: DepositSettingsProps) => {
  const translations = useTranslations();
  const [isEditMode, setIsEditMode] = useState(false);
  const { isOpen: showHistory, onOpen: handleOpenHistory, onClose: handleCloseHistory } = useDisclosure();

  const {
    data: restaurantDepositSettings,
    isLoading: isLoadingDepositSettings,
    error: depositSettingsError,
  } = useRestaurantDepositSettings(restaurantId);

  const model = useMemo(
    () => (restaurantDepositSettings ? convertResponseToFormValues(restaurantDepositSettings) : undefined),
    [restaurantDepositSettings],
  );

  const handleEditMode = () => setIsEditMode(true);

  return (
    <>
      <PageHeading>{translations('restaurant_deposit_header')}</PageHeading>
      <FullPageLoader show={isLoadingDepositSettings}>
        <ErrorMessages errors={depositSettingsError} />
        <UpdateDepositSettings
          restaurantId={restaurantId}
          model={model}
          isEditMode={isEditMode}
          onEditModeChange={setIsEditMode}
        />
        {!isEditMode ? (
          <Flex justifyContent="flex-start">
            <Button variant="primary" leftIcon={<Icon as={EditIcon} />} onClick={handleEditMode} mr={2}>
              {translations('edit')}
            </Button>
            <Button variant="outline" onClick={handleOpenHistory}>
              {translations('deposit_settings_show_history')}
            </Button>
          </Flex>
        ) : null}

        <Modal
          size="5xl"
          isModalVisible={showHistory}
          onModalClose={handleCloseHistory}
          customModalFooter={null}
          modalHeader={translations('deposit_settings_history_header')}
        >
          <DepositSettingsHistory history={model?.history || []} />
        </Modal>
      </FullPageLoader>
    </>
  );
};
